import { HierarchyPointNode } from "d3-hierarchy";
import { BaseType, Selection } from "d3-selection";
import { formatAttainmentProgressValue } from "@gtmhub/okrs/metrics/utils";
import { isIEBrowser } from "@gtmhub/shared/utils";
import { ICrossContributorLink, IItem, IItemId, ItemType, TreeViewMode } from "../models";

export interface ICoordinates {
  x: number;
  y: number;
}

// Create shortcuts for d3 types as they can be very long
export type MySelection<Datum> = Selection<BaseType, Datum, BaseType, unknown>;
export type MyHierarchyPointNodeSelection<Datum> = MySelection<MyHierarchyPointNode<Datum>>;
export interface MyHierarchyPointNode<N> extends HierarchyPointNode<N> {
  title?: IMyHierarchyPointNodeTitle;
}
export interface IMyHierarchyPointNodeTitle {
  wrap?: string[];
  overflow?: boolean;
  lines?: number;
}

export const percentageText = (n: number): string => `${formatAttainmentProgressValue(n)}%`;

export const cssClass = (obj: { [key: string]: boolean }): string =>
  Object.keys(obj)
    .filter((x) => obj[x])
    .join(" ");

export type WrapTitleConstants = {
  lineLength: number;
  maxLines: number;
  fontSize: number;
  fontWeight: number;
  lineHeight: number;
  titleWidth: string;
  ownersCount: number;
};

const buildTitleLines = (title: string, lineLength: number): string[] => {
  const splitTitle = title.split(" ").filter((word) => !!word.trim());
  const lines: string[] = [];
  if (!splitTitle.length) {
    return lines;
  }

  let line = splitTitle.splice(0, 1)[0];

  for (const word of splitTitle) {
    if (line.length + word.length < lineLength) {
      line = `${line} ${word}`;
    } else {
      lines.push(line);
      line = word;
      // Length of second line is bigger, because it doesn't have the owner's avatar
      lineLength += 100;
    }
  }
  lines.push(line);

  return lines;
};

function getSymbolsInfo(title: string, ownersCount: number, font: { size: number; weight: number }): { maxSymbols: number; avgSymbolSize: number } {
  const span = document.createElement("span");
  span.innerText = title;
  span.id = "just-one-random-id-135496-test";
  span.style.fontSize = font.size + "px";
  span.style.fontWeight = font.weight.toString();

  document.body.append(span);
  const elemWidth = document.querySelector("#just-one-random-id-135496-test").getBoundingClientRect().width;
  span.remove();

  const avgSymbolSize = +(elemWidth / title.length);
  const firstRowMaxWidth = 250;
  const ownerAvatarWidth = 23;
  const maxSymbols = Math.floor((firstRowMaxWidth - ownersCount * ownerAvatarWidth) / avgSymbolSize);

  return { maxSymbols, avgSymbolSize };
}

export const wrapTitle = (title: string, constants: WrapTitleConstants): IMyHierarchyPointNodeTitle => {
  if (!title) {
    return { wrap: [], overflow: false, lines: 0 };
  }
  const { maxLines, fontSize, fontWeight, lineHeight, titleWidth, ownersCount } = constants;
  const { maxSymbols, avgSymbolSize } = getSymbolsInfo(title, ownersCount, { size: fontSize, weight: fontWeight });

  let wrap = buildTitleLines(title, maxSymbols);
  let overflow = false;
  let lines = wrap.length;
  const secondRowMaxSymbols = Math.ceil(320 / avgSymbolSize);
  // Keep both "if" because of IE11
  if (wrap.length > maxLines) {
    overflow = true;
    wrap = wrap.slice(0, maxLines);
    lines = maxLines;
  }

  if (wrap[0].length > maxSymbols) {
    wrap[0] = wrap[0].slice(0, maxSymbols) + "...";
  }

  if (wrap[maxLines - 1] && wrap[maxLines - 1].length > secondRowMaxSymbols) {
    wrap[maxLines - 1] = wrap[maxLines - 1].slice(0, secondRowMaxSymbols) + "...";
  }

  const isIE = isIEBrowser();

  // modern browsers
  if (!isIE) {
    const el = document.createElement("div");
    el.innerText = title;
    el.style.width = titleWidth;
    // .goal-title-text styles
    el.style.fontSize = `${fontSize}px`;
    el.style.lineHeight = `${lineHeight}px`;
    el.style.fontWeight = `${fontWeight}`;
    el.style.hyphens = "auto";

    document.body.appendChild(el);
    const strHeight = window.getComputedStyle(el).height;
    const height = +strHeight.slice(0, strHeight.length - 2); // remove "px"

    overflow = false;
    lines = height / lineHeight < wrap.length ? wrap.length : height / lineHeight;

    if (lines > maxLines) {
      overflow = true;
      lines = maxLines;
    }

    el.remove();
  }

  return { wrap, overflow, lines };
};

export const itemEquals = (a: IItemId, b: IItemId): boolean => a.type === b.type && a.id === b.id;

export const nodeEquals = (a: { id: string; type: NodeType }, b: { id: string; type: NodeType }): boolean => a.type === b.type && a.id === b.id;

export type RootNodeType = "root";

export interface IRootNode {
  id: "root";
  type: RootNodeType;
  children: IItem[];
}

export const createRootDataNode = (dataNodes: IItem[]): IRootNode => ({
  id: "root",
  type: "root",
  children: dataNodes,
});

type ShowMoreNodeType = "show-more";

export interface IShowMoreNode {
  id: string;
  type: ShowMoreNodeType;
  title: string;
  obfuscated?: boolean;
  parent: {
    id: string;
    type: RootNodeType | ItemType;
  };
}

export type Node = IRootNode | IShowMoreNode | IItem;

type NodeType = ItemType | ShowMoreNodeType | RootNodeType;

export interface IContributorSubnode {
  node: MyHierarchyPointNode<IItem>;
  contributor: IItem;
  previousAdditionalTitleLines: number;
}

export interface ICrossContributorLinkEnd {
  node: MyHierarchyPointNode<IItem>;
  contributor: IItem;
  link: ICrossContributorLink;
}

export interface IMergedCrossContributorLink {
  source: ICrossContributorLinkEnd;
  target: ICrossContributorLinkEnd;
}

export const childrenCount = (item: IItem): number => item.contributors.length + item.supporters.length;

export interface IRenderingConstants {
  progressLabelPositionY: { objective: number; contributor: number; obfcustateObjective };
  progressLabelOffsetPositionX: number;
  contentHeightOffset: number;
  rowHeight: number;
  columnWidth: number;
  ownerNameHeight: number;
  ownersNamesHeight: number;
  linkWidth: number;
  itemWidth: number;
  itemTitlePadding: number;
  xCompensationForHiddenFakeRoot: number;
  showContributors: boolean;
  showOnlyContributorsOfTypeGoal: boolean;
  xTitle: number;
  titleWidth: number;
  titleWrap: {
    lineLength: number;
    maxLines: number;
  };
  itemTitleStyles: TitleStyles;
  contributorTitleStyles: TitleStyles;
  contributorTitleMaxWordLength: number;
  itemAvatarWidth: number;
  contributorAvatarWidth: number;
  itemTitleMaxWordLength: number;
}

interface TitleStyles {
  lineHeight: number;
  fontSize: number;
  fontWeight: number;
}

export function rendering(): Record<TreeViewMode, IRenderingConstants> {
  return {
    compact: {
      progressLabelPositionY: {
        objective: 25,
        contributor: 2,
        obfcustateObjective: 22,
      },
      progressLabelOffsetPositionX: 10,
      rowHeight: 45,
      contentHeightOffset: 5,
      columnWidth: 610,
      ownerNameHeight: 0,
      ownersNamesHeight: 16,
      linkWidth: 100,
      itemWidth: 410,
      itemTitlePadding: 15,
      xCompensationForHiddenFakeRoot: 360,
      showContributors: true,
      showOnlyContributorsOfTypeGoal: true,
      xTitle: 36,
      titleWidth: 190,
      titleWrap: {
        lineLength: 50,
        maxLines: 2,
      },
      itemTitleStyles: {
        lineHeight: 20,
        fontSize: 15,
        fontWeight: 500,
      },
      contributorTitleStyles: {
        lineHeight: 18,
        fontSize: 13,
        fontWeight: 400,
      },
      contributorTitleMaxWordLength: 30,
      itemTitleMaxWordLength: 28,
      itemAvatarWidth: 24,
      contributorAvatarWidth: 24,
    },
    medium: {
      progressLabelPositionY: {
        objective: 25,
        contributor: 2,
        obfcustateObjective: 22,
      },
      progressLabelOffsetPositionX: 10,
      rowHeight: 45,
      contentHeightOffset: 5,
      columnWidth: 610,
      ownerNameHeight: 20,
      ownersNamesHeight: 16,
      linkWidth: 150,
      itemWidth: 410,
      itemTitlePadding: 15,
      xCompensationForHiddenFakeRoot: 610,
      showContributors: true,
      showOnlyContributorsOfTypeGoal: true,
      xTitle: 36,
      titleWidth: 190,
      titleWrap: {
        lineLength: 50,
        maxLines: 2,
      },
      itemTitleStyles: {
        lineHeight: 20,
        fontSize: 15,
        fontWeight: 500,
      },
      contributorTitleStyles: {
        lineHeight: 18,
        fontSize: 13,
        fontWeight: 400,
      },
      contributorTitleMaxWordLength: 30,
      itemTitleMaxWordLength: 28,
      itemAvatarWidth: 24,
      contributorAvatarWidth: 24,
    },
    relaxed: {
      progressLabelPositionY: {
        objective: 25,
        contributor: 2,
        obfcustateObjective: 22,
      },
      progressLabelOffsetPositionX: 10,
      rowHeight: 45,
      contentHeightOffset: 5,
      columnWidth: 610,
      ownerNameHeight: 20,
      ownersNamesHeight: 16,
      linkWidth: 200,
      itemWidth: 410,
      itemTitlePadding: 15,
      xCompensationForHiddenFakeRoot: 610,
      showContributors: true,
      showOnlyContributorsOfTypeGoal: false,
      xTitle: 36,
      titleWidth: 190,
      titleWrap: {
        lineLength: 50,
        maxLines: 2,
      },
      itemTitleStyles: {
        lineHeight: 20,
        fontSize: 15,
        fontWeight: 500,
      },
      contributorTitleStyles: {
        lineHeight: 18,
        fontSize: 13,
        fontWeight: 400,
      },
      contributorTitleMaxWordLength: 30,
      itemTitleMaxWordLength: 28,
      itemAvatarWidth: 24,
      contributorAvatarWidth: 24,
    },
  };
}

export function wheelDelta(event: WheelEvent, currentScale: number): number {
  const deltaY = Math.abs(event.deltaY) > 100 ? event.deltaY / 10 : event.deltaY;
  const minZoomStep = 0.02;
  let zoomStep = Math.round(Math.abs(deltaY) * 0.17) / 100;
  if (zoomStep < minZoomStep) {
    zoomStep = minZoomStep;
  }
  const neededScale = Math.round((currentScale + (event.deltaY > 0 ? -zoomStep : zoomStep)) * 100) / 100;
  const neededMultiplier = neededScale / currentScale;
  return Math.log(neededMultiplier) / Math.log(2);
}
